import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Server,
  Users,
  Bot,
  Calendar,
  Download,
  RefreshCw,
  ChevronDown,
  Pause,
  Play,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building2,
  BarChart3,
  PieChart,
  Plus,
  Edit2,
  Trash2,
  X,
  Activity
} from 'lucide-react'
import { financialsApi } from '../services/api'
import './Financials.css'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  }
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

function Financials() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [dashboard, setDashboard] = useState(null)
  const [clientRevenue, setClientRevenue] = useState([])
  const [servers, setServers] = useState([])
  const [requestStats, setRequestStats] = useState(null)

  // Tab selection
  const [activeTab, setActiveTab] = useState('overview')

  // Period selection
  const currentDate = new Date()
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false)

  // Server edit modal
  const [showServerModal, setShowServerModal] = useState(false)
  const [editingServer, setEditingServer] = useState(null)
  const [serverFormData, setServerFormData] = useState({
    server_ip: '',
    provider: 'Hetzner',
    monthly_cost: '',
    setup_fee: '0',
    max_capacity: '40',
    started_at: '',
    notes: ''
  })

  useEffect(() => {
    loadData()
  }, [selectedMonth, selectedYear])

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const [dashboardRes, clientsRes, serversRes, statsRes] = await Promise.allSettled([
        financialsApi.getDashboard(selectedMonth, selectedYear),
        financialsApi.getRevenueByClient(selectedMonth, selectedYear),
        financialsApi.getServers(),
        financialsApi.getRequestStats()
      ])

      if (dashboardRes.status === 'fulfilled') {
        setDashboard(dashboardRes.value.data)
      }
      if (clientsRes.status === 'fulfilled') {
        setClientRevenue(clientsRes.value.data.clients || [])
      }
      if (serversRes.status === 'fulfilled') {
        setServers(serversRes.value.data.servers || [])
      }
      if (statsRes.status === 'fulfilled') {
        setRequestStats(statsRes.value.data.stats || null)
      }
    } catch (error) {
      console.error('Failed to load financial data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0)
  }

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0)
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', label: 'Active' },
      suspended: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'Suspended' },
      pending: { bg: 'rgba(234, 179, 8, 0.15)', color: '#eab308', label: 'Pending' },
      paused: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Paused' }
    }
    const style = styles[status] || styles.pending
    return (
      <span className="status-badge" style={{ background: style.bg, color: style.color }}>
        {style.label}
      </span>
    )
  }

  const getUtilizationColor = (pct) => {
    const value = parseFloat(pct)
    if (value >= 90) return '#22c55e'
    if (value >= 70) return '#eab308'
    if (value >= 50) return '#f97316'
    return '#ef4444'
  }

  // Server modal handlers
  const handleOpenServerModal = (server = null) => {
    if (server) {
      setEditingServer(server)
      setServerFormData({
        server_ip: server.server_ip,
        provider: server.provider || 'Hetzner',
        monthly_cost: server.monthly_cost?.toString() || '',
        setup_fee: server.setup_fee?.toString() || '0',
        max_capacity: server.max_capacity?.toString() || '40',
        started_at: server.started_at?.split('T')[0] || '',
        notes: server.notes || ''
      })
    } else {
      setEditingServer(null)
      setServerFormData({
        server_ip: '',
        provider: 'Hetzner',
        monthly_cost: '120',
        setup_fee: '0',
        max_capacity: '40',
        started_at: new Date().toISOString().split('T')[0],
        notes: ''
      })
    }
    setShowServerModal(true)
  }

  const handleCloseServerModal = () => {
    setShowServerModal(false)
    setEditingServer(null)
  }

  const handleServerSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingServer) {
        await financialsApi.updateServer(editingServer.id, {
          monthly_cost: parseFloat(serverFormData.monthly_cost),
          max_capacity: parseInt(serverFormData.max_capacity),
          notes: serverFormData.notes
        })
      } else {
        await financialsApi.addServer({
          server_ip: serverFormData.server_ip,
          provider: serverFormData.provider,
          monthly_cost: parseFloat(serverFormData.monthly_cost),
          setup_fee: parseFloat(serverFormData.setup_fee),
          max_capacity: parseInt(serverFormData.max_capacity),
          started_at: serverFormData.started_at,
          notes: serverFormData.notes
        })
      }
      handleCloseServerModal()
      loadData(true)
    } catch (error) {
      console.error('Failed to save server:', error)
      alert('Failed to save server: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleCancelServer = async (id) => {
    if (!confirm('Are you sure you want to cancel this server? This will mark it as inactive.')) {
      return
    }
    try {
      await financialsApi.cancelServer(id)
      loadData(true)
    } catch (error) {
      console.error('Failed to cancel server:', error)
      alert('Failed to cancel server')
    }
  }

  // Server stats calculation
  const getServerStats = () => {
    const active = servers.filter(s => s.status === 'active')
    return {
      totalServers: active.length,
      totalCost: active.reduce((sum, s) => sum + (parseFloat(s.monthly_cost) || 0), 0),
      totalCapacity: active.reduce((sum, s) => sum + (s.max_capacity || 0), 0),
      totalBots: active.reduce((sum, s) => sum + (s.active_bots || 0), 0)
    }
  }

  const serverStats = getServerStats()
  const avgUtilization = serverStats.totalCapacity > 0
    ? ((serverStats.totalBots / serverStats.totalCapacity) * 100).toFixed(1)
    : 0

  if (loading) {
    return (
      <div className="financials-page">
        <div className="loading-state">
          <RefreshCw className="spin" size={32} />
          <p>Loading financial data...</p>
        </div>
      </div>
    )
  }

  return (
    <>
    <motion.div
      className="financials-page"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="page-header" variants={itemVariants}>
        <div className="header-left">
          <h1>Financial Dashboard</h1>
          <p className="header-subtitle">P&L Overview and Revenue Analytics</p>
        </div>
        <div className="header-actions">
          {/* Period Selector - only show for Overview tab */}
          {activeTab === 'overview' && (
            <div className="period-selector">
              <button
                className="period-btn"
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
              >
                <Calendar size={16} />
                {MONTHS[selectedMonth - 1]} {selectedYear}
                <ChevronDown size={14} />
              </button>
              {showPeriodDropdown && (
                <>
                  <div className="dropdown-backdrop" onClick={() => setShowPeriodDropdown(false)} />
                  <div className="period-dropdown">
                    <div className="period-months">
                      {MONTHS.map((month, idx) => (
                        <button
                          key={month}
                          className={selectedMonth === idx + 1 ? 'active' : ''}
                          onClick={() => {
                            setSelectedMonth(idx + 1)
                            setShowPeriodDropdown(false)
                          }}
                        >
                          {month.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                    <div className="period-years">
                      {[2024, 2025, 2026].map(year => (
                        <button
                          key={year}
                          className={selectedYear === year ? 'active' : ''}
                          onClick={() => {
                            setSelectedYear(year)
                            setShowPeriodDropdown(false)
                          }}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Add Server button - only show for Server Costs tab */}
          {activeTab === 'servers' && (
            <button className="btn btn-primary" onClick={() => handleOpenServerModal()}>
              <Plus size={16} />
              Add Server
            </button>
          )}

          <button
            className="btn btn-ghost"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div className="financials-tabs" variants={itemVariants}>
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          P&L Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'servers' ? 'active' : ''}`}
          onClick={() => setActiveTab('servers')}
        >
          <Server size={16} />
          Server Costs
        </button>
      </motion.div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <>
          {/* Summary Stats */}
          <motion.div className="stat-blocks" variants={itemVariants}>
            <div className="stat-block revenue">
              <div className="stat-icon">
                <DollarSign size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{formatCurrency(dashboard?.summary?.revenue)}</span>
                <span className="stat-label">Revenue (MTD)</span>
                <span className="stat-rate">${(dashboard?.summary?.hourlyRevenue || 0).toFixed(3)}/hr</span>
              </div>
            </div>
            <div className="stat-block costs">
              <div className="stat-icon">
                <Server size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{formatCurrency(dashboard?.summary?.costs)}</span>
                <span className="stat-label">Costs (MTD)</span>
                <span className="stat-rate">${(dashboard?.summary?.hourlyCosts || 0).toFixed(3)}/hr</span>
              </div>
            </div>
            <div className="stat-block profit">
              <div className="stat-icon">
                <TrendingUp size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{formatCurrency(dashboard?.summary?.profit)}</span>
                <span className="stat-label">Profit (MTD)</span>
                <span className="stat-rate">${(dashboard?.summary?.hourlyProfit || 0).toFixed(3)}/hr</span>
              </div>
            </div>
            <div className="stat-block margin">
              <div className="stat-icon">
                <PieChart size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{formatCurrency(dashboard?.summary?.monthlyEstimate)}</span>
                <span className="stat-label">Monthly Est.</span>
                <span className="stat-rate">{dashboard?.summary?.margin || 0}% margin</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Stats Row */}
          <motion.div className="quick-stats" variants={itemVariants}>
            <div className="quick-stat">
              <Users size={18} />
              <span className="quick-stat-value">{dashboard?.clients?.active || 0}</span>
              <span className="quick-stat-label">Active Clients</span>
            </div>
            <div className="quick-stat">
              <Bot size={18} />
              <span className="quick-stat-value">{dashboard?.bots?.active || 0}</span>
              <span className="quick-stat-label">Active Bots</span>
            </div>
            <div className="quick-stat">
              <Server size={18} />
              <span className="quick-stat-value">{dashboard?.servers?.count || 0}</span>
              <span className="quick-stat-label">Servers</span>
            </div>
            <div className="quick-stat warning">
              <AlertTriangle size={18} />
              <span className="quick-stat-value">{formatCurrency(dashboard?.waste?.mtd || 0)}</span>
              <span className="quick-stat-label">Wasted (MTD)</span>
            </div>
            {requestStats && requestStats.pending > 0 && (
              <div className="quick-stat">
                <Clock size={18} />
                <span className="quick-stat-value">{requestStats.pending}</span>
                <span className="quick-stat-label">Pending Requests</span>
              </div>
            )}
          </motion.div>

          {/* Two Column Layout */}
          <div className="financials-grid">
            {/* Revenue by Client */}
            <motion.div className="financials-card" variants={itemVariants}>
              <div className="card-header">
                <h2><Building2 size={18} /> Revenue by Client</h2>
              </div>
              <div className="card-body">
                {clientRevenue.length === 0 ? (
                  <div className="empty-state">
                    <Users size={32} />
                    <p>No client data for this period</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th className="text-center">Bots</th>
                        <th className="text-center">Rate/hr</th>
                        <th className="text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientRevenue.map(client => (
                        <tr key={client.id}>
                          <td>
                            <div className="client-info">
                              <span className="client-name">{client.name}</span>
                              {client.company && (
                                <span className="client-company">{client.company}</span>
                              )}
                            </div>
                          </td>
                          <td className="text-center">{client.active_bot_count || 0}</td>
                          <td className="text-center">${((client.default_bot_price || 15) / 720).toFixed(3)}</td>
                          <td className="text-right revenue-cell">
                            {formatCurrency(client.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </motion.div>

            {/* Server Utilization */}
            <motion.div className="financials-card" variants={itemVariants}>
              <div className="card-header">
                <h2><Server size={18} /> Server Utilization</h2>
              </div>
              <div className="card-body">
                {servers.length === 0 ? (
                  <div className="empty-state">
                    <Server size={32} />
                    <p>No active servers found</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Server</th>
                        <th className="text-right">Cost/hr</th>
                        <th className="text-center">Bots</th>
                        <th className="text-center">Util</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servers.map((server, idx) => (
                        <tr key={server.server_ip || idx}>
                          <td>
                            <span className="server-ip">{server.server_ip}</span>
                          </td>
                          <td className="text-right">${(server.monthly_cost / 720).toFixed(3)}</td>
                          <td className="text-center">
                            {server.active_bots}/{server.max_capacity}
                          </td>
                          <td className="text-center">
                            <div className="utilization-bar">
                              <div
                                className="utilization-fill"
                                style={{
                                  width: `${server.utilization}%`,
                                  backgroundColor: getUtilizationColor(server.utilization)
                                }}
                              />
                              <span className="utilization-text">{server.utilization}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </motion.div>
          </div>
        </>
      ) : (
        /* Server Costs Tab */
        <>
          {/* Server Stats */}
          <motion.div className="stat-blocks" variants={itemVariants}>
            <div className="stat-block blue">
              <div className="stat-icon">
                <Server size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{serverStats.totalServers}</span>
                <span className="stat-label">Active Servers</span>
              </div>
            </div>
            <div className="stat-block orange">
              <div className="stat-icon">
                <DollarSign size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{formatCurrency(serverStats.totalCost)}</span>
                <span className="stat-label">Monthly Cost</span>
              </div>
            </div>
            <div className="stat-block green">
              <div className="stat-icon">
                <Activity size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{serverStats.totalBots} / {serverStats.totalCapacity}</span>
                <span className="stat-label">Bots / Capacity</span>
              </div>
            </div>
            <div className="stat-block purple">
              <div className="stat-icon">
                <Activity size={20} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{avgUtilization}%</span>
                <span className="stat-label">Avg Utilization</span>
              </div>
            </div>
          </motion.div>

          {/* Server Table */}
          <motion.div className="financials-card full-width" variants={itemVariants}>
            <div className="card-header">
              <h2><Server size={18} /> Server Infrastructure</h2>
            </div>
            <div className="card-body">
              {servers.length === 0 ? (
                <div className="empty-state">
                  <Server size={32} />
                  <p>No servers configured</p>
                  <button className="btn btn-primary" onClick={() => handleOpenServerModal()}>
                    Add First Server
                  </button>
                </div>
              ) : (
                <div className="table-scroll-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Server IP</th>
                        <th>Provider</th>
                        <th className="text-right">Monthly Cost</th>
                        <th className="text-center">Capacity</th>
                        <th className="text-center">Active Bots</th>
                        <th className="text-center">Utilization</th>
                        <th className="text-center">Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servers.map(server => (
                        <tr key={server.id || server.server_ip} className={server.status !== 'active' ? 'inactive-row' : ''}>
                          <td>
                            <span className="server-ip">{server.server_ip}</span>
                          </td>
                          <td>{server.provider || 'Hetzner'}</td>
                          <td className="text-right">{formatCurrency(server.monthly_cost)}</td>
                          <td className="text-center">{server.max_capacity}</td>
                          <td className="text-center">{server.active_bots || 0}</td>
                          <td className="text-center">
                            <div className="utilization-bar">
                              <div
                                className="utilization-fill"
                                style={{
                                  width: `${server.utilization || 0}%`,
                                  backgroundColor: getUtilizationColor(server.utilization)
                                }}
                              />
                              <span className="utilization-text">{server.utilization || 0}%</span>
                            </div>
                          </td>
                          <td className="text-center">
                            {server.status === 'active' ? (
                              <CheckCircle size={16} className="text-success" />
                            ) : (
                              <AlertTriangle size={16} className="text-warning" />
                            )}
                          </td>
                          <td className="text-right">
                            <div className="action-buttons">
                              <button
                                className="btn btn-icon btn-ghost"
                                onClick={() => handleOpenServerModal(server)}
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </button>
                              {server.status === 'active' && (
                                <button
                                  className="btn btn-icon btn-ghost btn-danger"
                                  onClick={() => handleCancelServer(server.id)}
                                  title="Cancel"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}

    </motion.div>

      {/* Server Edit Modal */}
      <AnimatePresence>
        {showServerModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseServerModal}
          >
            <motion.div
              className="modal server-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>{editingServer ? 'Edit Server' : 'Add Server'}</h3>
                <button className="btn btn-icon btn-ghost" onClick={handleCloseServerModal}>
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleServerSubmit}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Server IP</label>
                      <input
                        type="text"
                        value={serverFormData.server_ip}
                        onChange={e => setServerFormData({ ...serverFormData, server_ip: e.target.value })}
                        placeholder="10.10.10.X"
                        disabled={!!editingServer}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Provider</label>
                      <select
                        value={serverFormData.provider}
                        onChange={e => setServerFormData({ ...serverFormData, provider: e.target.value })}
                      >
                        <option value="Hetzner">Hetzner</option>
                        <option value="OVH">OVH</option>
                        <option value="AWS">AWS</option>
                        <option value="Azure">Azure</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Monthly Cost ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={serverFormData.monthly_cost}
                        onChange={e => setServerFormData({ ...serverFormData, monthly_cost: e.target.value })}
                        placeholder="120.00"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Setup Fee ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={serverFormData.setup_fee}
                        onChange={e => setServerFormData({ ...serverFormData, setup_fee: e.target.value })}
                        placeholder="0.00"
                        disabled={!!editingServer}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Max Capacity (bots)</label>
                      <input
                        type="number"
                        value={serverFormData.max_capacity}
                        onChange={e => setServerFormData({ ...serverFormData, max_capacity: e.target.value })}
                        placeholder="40"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Started At</label>
                      <input
                        type="date"
                        value={serverFormData.started_at}
                        onChange={e => setServerFormData({ ...serverFormData, started_at: e.target.value })}
                        disabled={!!editingServer}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea
                      value={serverFormData.notes}
                      onChange={e => setServerFormData({ ...serverFormData, notes: e.target.value })}
                      placeholder="Optional notes..."
                      rows={2}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={handleCloseServerModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingServer ? 'Save Changes' : 'Add Server'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Financials
